import React from 'react';

import {ChartConfiguration} from 'chart.js';
import {useTranslation} from 'react-i18next';

import {AuditStatisticsEntity, VIOLATION_STATUS_CODE} from '@entities/audit-statistics/entity';
import {ViolationDictionaryItem} from '@entities/violations/types';

import {ChartBox, ChartHeader, ChartTitle, Legend, LegendDot, LegendItem} from '../styled';

import {createChart, getLocalizedTitle, MONTH_KEYS} from './chartUtils';

interface Props {
    statistics: AuditStatisticsEntity;
    violationStatuses: ViolationDictionaryItem[];
}

export const TaxPayersByViolationStatusChart = ({statistics, violationStatuses}: Props) => {
    const {t, i18n} = useTranslation();
    const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
    const normalizedData = React.useMemo(() => statistics.normalizeTaxPayersByViolationStatus(), [
        statistics,
    ]);
    const selectedMonths = normalizedData.map((item) => item.month);
    const labels = [VIOLATION_STATUS_CODE.OPENED, VIOLATION_STATUS_CODE.CLOSED].map(
        (code) =>
            getLocalizedTitle(
                violationStatuses.find((item) => item.code === code),
                i18n.language,
            ) ||
            t(`page_audit.page_statistic.tax-payers-by-violation-status-${code.toLowerCase()}`),
    );

    React.useEffect(() => {
        if (!canvasRef.current) return undefined;

        const config: ChartConfiguration<'bar'> = {
            type: 'bar',
            data: {
                labels: MONTH_KEYS.filter((_, index) =>
                    selectedMonths.includes(index + 1),
                ).map((month) => t(`dictionaries.months.${month}.short`)),
                datasets: [
                    {
                        label: labels[0],
                        data: normalizedData.map((item) => item[VIOLATION_STATUS_CODE.OPENED]),
                        backgroundColor: '#3AA1EC',
                    },
                    {
                        label: labels[1],
                        data: normalizedData.map((item) => item[VIOLATION_STATUS_CODE.CLOSED]),
                        backgroundColor: '#BAC0C8',
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {display: false},
                    tooltip: {
                        padding: 16,
                        callbacks: {
                            label: () => '',
                            beforeLabel: (item) =>
                                `${labels[item.datasetIndex]}: ${item.formattedValue}`,
                        },
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {precision: 0},
                    },
                },
            },
        };

        const chart = createChart(canvasRef.current, config);
        return () => chart.destroy();
    }, [labels, normalizedData, selectedMonths, t]);

    return (
        <>
            <ChartHeader>
                <ChartTitle>
                    {t('page_audit.page_statistic.tax-payers-by-violation-status-title')}
                </ChartTitle>
                <Legend>
                    <LegendItem>
                        <LegendDot color="#3AA1EC" />
                        {labels[0]}
                    </LegendItem>
                    <LegendItem>
                        <LegendDot color="#BAC0C8" />
                        {labels[1]}
                    </LegendItem>
                </Legend>
            </ChartHeader>
            <ChartBox>
                <canvas ref={canvasRef} />
            </ChartBox>
        </>
    );
};
